import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useKioskInit, useAvailability, createKioskBooking, fontMoodToFamily, getDynamicPrice, checkIsNewClient } from './hooks';
import type { KioskConfig, KioskService, KioskOperator, KioskHours, DynamicPriceResult } from './hooks';
import { KioskFooter } from './KioskFooter';
import { KioskServicePicker } from './KioskServicePicker';
import { KioskOperatorPicker } from './KioskOperatorPicker';
import { KioskDateTimePicker } from './KioskDateTimePicker';
import { KioskClientForm } from './KioskClientForm';
import { KioskConfirmation } from './KioskConfirmation';
import { KioskSuccess } from './KioskSuccess';
import { KioskTestimonials } from './KioskTestimonials';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

type Step = 'landing' | 'services' | 'operator' | 'datetime' | 'client' | 'confirm' | 'success';

function todayStr() { return new Date().toISOString().split('T')[0]; }

// ── Shared helpers ──

function initialsColor(name: string): string {
  const colors = ['#6CB4EE', '#12B76A', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function initials(name: string): string {
  return name.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
}

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function fmtHours(t: string) {
  const [hh, mm] = t.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h = hh % 12 || 12;
  return mm ? `${h}:${mm.toString().padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
}

// ══════════════════════════════════════════════════
// LANDING PAGE
// ══════════════════════════════════════════════════

function LandingPage({ config, services, operators, hours, headingFont, onBookNow, onSelectService, onSelectOperator }: {
  config: KioskConfig; services: KioskService[]; operators: KioskOperator[];
  hours: KioskHours[]; headingFont: string;
  onBookNow: () => void; onSelectService: (s: KioskService) => void; onSelectOperator: (o: KioskOperator) => void;
}) {
  const accent = config.accentColor;
  const today = new Date().getDay();
  const showTeam = config.showOperators && operators.length > 1;
  const hasAddress = config.address || config.city;

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* ── Hero ── */}
      <div style={{
        position: 'relative', minHeight: 320, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '48px 24px 40px',
        background: config.coverPhotoUrl
          ? `linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.5)), url(${config.coverPhotoUrl}) center/cover no-repeat`
          : `linear-gradient(135deg, ${accent}18, ${accent}08 40%, #fff 100%)`,
        color: config.coverPhotoUrl ? '#fff' : '#1A1A1A',
        textAlign: 'center',
      }}>
        {/* Logo */}
        {config.logoUrl ? (
          <img src={config.logoUrl} alt="" style={{ width: 72, height: 72, borderRadius: 20, objectFit: 'cover', marginBottom: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }} />
        ) : (
          <div style={{
            width: 72, height: 72, borderRadius: 20, background: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 28, fontWeight: 700, fontFamily: headingFont,
            marginBottom: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}>
            {config.businessName[0]?.toUpperCase()}
          </div>
        )}
        <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: headingFont, margin: '0 0 4px', lineHeight: 1.2 }}>
          {config.businessName}
        </h1>
        {config.headline && (
          <p style={{ fontSize: 15, opacity: 0.85, margin: '4px 0 0', fontFamily: BODY_FONT, maxWidth: 360, lineHeight: 1.5 }}>
            {config.headline}
          </p>
        )}
        <button onClick={onBookNow} style={{
          marginTop: 24, padding: '14px 40px', borderRadius: 24, border: 'none',
          background: accent, color: '#fff', fontSize: 16, fontWeight: 600,
          fontFamily: BODY_FONT, cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transition: 'transform 150ms ease, box-shadow 150ms ease',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }}
        >
          Book Now
        </button>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
        {/* ── About ── */}
        {config.description && (
          <section style={{ padding: '32px 0', borderBottom: '1px solid #F1F3F5' }}>
            <p style={{ fontSize: 15, color: '#636366', lineHeight: 1.7, fontFamily: BODY_FONT, margin: 0 }}>
              {config.description}
            </p>
          </section>
        )}

        {/* ── Team ── */}
        {showTeam && (
          <section style={{ padding: '32px 0', borderBottom: '1px solid #F1F3F5' }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 16px' }}>
              Meet the team
            </h2>
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
              {operators.map(op => (
                <div key={op.id} style={{
                  minWidth: 160, maxWidth: 200, background: '#FAFAFA', borderRadius: 14,
                  padding: 20, textAlign: 'center', flexShrink: 0,
                  border: '1px solid #F1F3F5', transition: 'border-color 200ms ease',
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = accent; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#F1F3F5'; }}
                >
                  {op.avatar_url ? (
                    <img src={op.avatar_url} alt="" style={{ width: 56, height: 56, borderRadius: 28, objectFit: 'cover', margin: '0 auto 10px' }} />
                  ) : (
                    <div style={{
                      width: 56, height: 56, borderRadius: 28, background: initialsColor(op.name),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 18, fontWeight: 600, fontFamily: BODY_FONT, margin: '0 auto 10px',
                    }}>{initials(op.name)}</div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', fontFamily: BODY_FONT }}>{op.name}</div>
                  {op.bio && <div style={{ fontSize: 12, color: '#636366', marginTop: 4, lineHeight: 1.4, fontFamily: BODY_FONT, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{op.bio}</div>}
                  <button onClick={() => onSelectOperator(op)} style={{
                    marginTop: 10, fontSize: 13, fontWeight: 600, color: accent, background: 'none',
                    border: 'none', cursor: 'pointer', fontFamily: BODY_FONT, padding: 0,
                  }}>
                    Book with {op.name.split(' ')[0]}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Intro Offers ── */}
        {services.some(s => s.intro_enabled && s.intro_price != null) && (
          <section style={{ padding: '32px 0', borderBottom: '1px solid #F1F3F5' }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 6px' }}>
              New here? Try these offers
            </h2>
            <p style={{ fontSize: 13, color: '#636366', fontFamily: BODY_FONT, margin: '0 0 16px' }}>First-visit pricing for new clients</p>
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
              {services.filter(s => s.intro_enabled && s.intro_price != null).map(svc => (
                <button key={svc.id} onClick={() => onSelectService(svc)} style={{
                  minWidth: 200, maxWidth: 240, background: '#fff', borderRadius: 14, padding: 18, border: `1px solid #F1F3F5`,
                  textAlign: 'left', cursor: 'pointer', flexShrink: 0, fontFamily: BODY_FONT, transition: 'border-color 200ms ease',
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = accent; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#F1F3F5'; }}
                >
                  <div style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#FFF1D6', color: '#92400E', display: 'inline-block', marginBottom: 8 }}>{svc.intro_label || 'First Visit Special'}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{svc.name}</div>
                  <div style={{ fontSize: 13, color: '#636366', marginTop: 2 }}>{svc.duration_minutes} min</div>
                  {config.showPrices && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A' }}>${Number(svc.intro_price!).toFixed(0)}</span>
                      <span style={{ fontSize: 13, color: '#ADB5BD', textDecoration: 'line-through' }}>${Number(svc.price).toFixed(0)}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#107C10' }}>Save ${(svc.price - svc.intro_price!).toFixed(0)}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Services ── */}
        <section style={{ padding: '32px 0', borderBottom: '1px solid #F1F3F5' }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 16px' }}>
            Services
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: '#F1F3F5', borderRadius: 14, overflow: 'hidden' }}>
            {services.map(svc => (
              <button key={svc.id} onClick={() => onSelectService(svc)} style={{
                background: '#fff', border: 'none', padding: '16px 18px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontFamily: BODY_FONT, textAlign: 'left', transition: 'background 150ms ease',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#FAFAFA'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 500, color: '#1A1A1A' }}>{svc.name}</span>
                    {svc.intro_enabled && svc.intro_price != null && (
                      <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#FFF1D6', color: '#92400E' }}>{svc.intro_label || 'Intro'}</span>
                    )}
                  </div>
                  {svc.description && <div style={{ fontSize: 13, color: '#636366', marginTop: 2 }}>{svc.description}</div>}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                  <div style={{ fontSize: 13, color: '#636366' }}>{svc.duration_minutes} min</div>
                  {config.showPrices && svc.intro_enabled && svc.intro_price != null ? (
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>${Number(svc.intro_price).toFixed(0)}</span>
                      <span style={{ fontSize: 12, color: '#ADB5BD', textDecoration: 'line-through', marginLeft: 4 }}>${Number(svc.price).toFixed(0)}</span>
                    </div>
                  ) : (
                    config.showPrices && <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>${Number(svc.price).toFixed(0)}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* ── Testimonials ── */}
        {config.showTestimonials && config.testimonials.length > 0 && (
          <KioskTestimonials testimonials={config.testimonials} accentColor={accent} headingFont={headingFont} />
        )}

        {/* ── Hours ── */}
        {hours.length > 0 && (
          <section style={{ padding: '32px 0', borderBottom: hasAddress ? '1px solid #F1F3F5' : 'none' }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 16px' }}>
              Hours
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hours.sort((a, b) => a.day_of_week - b.day_of_week).map(h => (
                <div key={h.day_of_week} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontFamily: BODY_FONT, fontSize: 14,
                }}>
                  <span style={{
                    color: h.day_of_week === today ? '#1A1A1A' : '#636366',
                    fontWeight: h.day_of_week === today ? 600 : 400,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    {h.day_of_week === today && <span style={{ width: 6, height: 6, borderRadius: 3, background: accent, display: 'inline-block' }} />}
                    {DAY_LABELS[h.day_of_week]}
                  </span>
                  <span style={{ color: h.is_open ? '#636366' : '#ADB5BD', fontWeight: h.day_of_week === today ? 600 : 400 }}>
                    {h.is_open ? `${fmtHours(h.open_time)} – ${fmtHours(h.close_time)}` : 'Closed'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Location ── */}
        {hasAddress && (
          <section style={{ padding: '32px 0' }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, fontFamily: headingFont, color: '#1A1A1A', margin: '0 0 12px' }}>
              Location
            </h2>
            <div style={{ fontSize: 14, color: '#636366', fontFamily: BODY_FONT, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 16, marginTop: 1 }}>📍</span>
              <span>{[config.address, config.city, config.province].filter(Boolean).join(', ')}</span>
            </div>
          </section>
        )}
      </div>

      <KioskFooter industry={config?.industry} />
    </div>
  );
}

// ══════════════════════════════════════════════════
// BOOKING FLOW HEADER (replaces old KioskHeader)
// ══════════════════════════════════════════════════

function BookingHeader({ config, step, totalSteps, headingFont, onBack }: {
  config: KioskConfig; step: number; totalSteps: number; headingFont: string; onBack: () => void;
}) {
  const progress = step / totalSteps;
  return (
    <div style={{ borderBottom: '1px solid #E5E5EA' }}>
      {/* Progress bar */}
      <div style={{ height: 3, background: '#F1F3F5' }}>
        <div style={{ height: 3, background: config.accentColor, width: `${progress * 100}%`, transition: 'width 300ms ease', borderRadius: '0 2px 2px 0' }} />
      </div>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          color: '#636366', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center',
        }} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
        </button>
        {config.logoUrl ? (
          <img src={config.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: 28, height: 28, borderRadius: 7, background: config.accentColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: headingFont,
          }}>
            {config.businessName[0]?.toUpperCase()}
          </div>
        )}
        <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', fontFamily: BODY_FONT, flex: 1 }}>
          {config.businessName}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════

export function KioskPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { config, services, operators, hours, loading, error } = useKioskInit(slug);

  const isEmbed = searchParams.get('embed') === 'true';
  const directMode = searchParams.get('direct') === 'true' || isEmbed;
  const preServiceId = searchParams.get('service');
  const preOperatorId = searchParams.get('operator');
  const contentRef = useRef<HTMLDivElement>(null);

  // Embed mode: send resize postMessages to parent window
  useEffect(() => {
    if (!isEmbed || !contentRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height) {
        window.parent.postMessage({ type: 'crewbit:resize', height: Math.ceil(height) }, '*');
      }
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isEmbed]);

  const [step, setStep] = useState<Step>('landing');
  const [selectedService, setSelectedService] = useState<KioskService | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<KioskOperator | null>(null);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dynamicPrice, setDynamicPrice] = useState<DynamicPriceResult | null>(null);
  const [isNewClient, setIsNewClient] = useState(true); // benefit of the doubt

  const skipOperator = !config?.showOperators || config?.operatorSelectionMode === 'hidden';
  const headingFont = fontMoodToFamily(config?.fontMood);

  // Handle query param deep links after config loads
  useEffect(() => {
    if (!config || !services.length) return;
    if (directMode && step === 'landing') {
      if (preServiceId) {
        const svc = services.find(s => s.id === preServiceId);
        if (svc) { setSelectedService(svc); setStep(skipOperator ? 'datetime' : 'operator'); return; }
      }
      setStep('services');
    }
    if (preOperatorId && operators.length) {
      const op = operators.find(o => o.id === preOperatorId);
      if (op) setSelectedOperator(op);
    }
  }, [config, services, operators, directMode, preServiceId, preOperatorId, skipOperator]);

  const { slots, closed, loading: slotsLoading } = useAvailability(
    config?.slug, selectedDate, selectedService?.id ?? null, selectedOperator?.id ?? null,
  );

  const totalSteps = skipOperator ? 4 : 5;
  const stepIndex = useMemo(() => {
    const map: Record<Step, number> = skipOperator
      ? { landing: 0, services: 1, operator: 1, datetime: 2, client: 3, confirm: 4, success: 5 }
      : { landing: 0, services: 1, operator: 2, datetime: 3, client: 4, confirm: 5, success: 6 };
    return map[step];
  }, [step, skipOperator]);

  // ── Navigation ──

  const enterBookingFlow = useCallback(() => { setStep('services'); }, []);

  const handleSelectServiceFromLanding = useCallback((svc: KioskService) => {
    setSelectedService(svc);
    setStep(skipOperator ? 'datetime' : 'operator');
  }, [skipOperator]);

  const handleSelectOperatorFromLanding = useCallback((op: KioskOperator) => {
    setSelectedOperator(op);
    setStep('services');
  }, []);

  const handleSelectService = useCallback((svc: KioskService) => {
    setSelectedService(svc);
    setStep(skipOperator ? 'datetime' : (selectedOperator ? 'datetime' : 'operator'));
    if (isEmbed) {
      window.parent.postMessage({ type: 'crewbit:booking:started', service: svc.name }, '*');
    }
  }, [skipOperator, selectedOperator, isEmbed]);

  const handleSelectOperator = useCallback((op: KioskOperator | null) => {
    setSelectedOperator(op);
    setStep('datetime');
  }, []);

  const handleSelectTime = useCallback(async (time: string) => {
    setSelectedTime(time);
    setStep('client');
    // Fetch dynamic price in background
    if (config && selectedService) {
      const dp = await getDynamicPrice(selectedService.id, selectedDate, time, config.businessId);
      setDynamicPrice(dp);
    }
  }, [config, selectedService, selectedDate]);

  const handleConfirm = useCallback(async () => {
    if (!config || !selectedService || !selectedTime) return;
    if (config.isDemo) {
      setSubmitError('This is a demo booking page. Create your own at crewbit.ca/notify');
      return;
    }
    setSubmitting(true); setSubmitError(null);
    const result = await createKioskBooking({
      slug: config.slug, serviceId: selectedService.id, date: selectedDate,
      startTime: selectedTime, clientName: clientName.trim(),
      clientPhone: clientPhone.trim(), notes: clientNotes.trim() || undefined,
    });
    if (result.success) {
      setStep('success');
      if (isEmbed) {
        window.parent.postMessage({ type: 'crewbit:booking:completed', bookingId: result.data?.booking_id ?? null }, '*');
      }
    } else setSubmitError(result.error ?? 'Something went wrong. Please try again.');
    setSubmitting(false);
  }, [config, selectedService, selectedDate, selectedTime, clientName, clientPhone, clientNotes]);

  const resetAll = useCallback(() => {
    setStep('landing'); setSelectedService(null); setSelectedOperator(null);
    setSelectedDate(todayStr()); setSelectedTime(null);
    setClientName(''); setClientPhone(''); setClientNotes('');
    setSubmitting(false); setSubmitError(null); setDynamicPrice(null); setIsNewClient(true);
  }, []);

  const handleBack = useCallback(() => {
    switch (step) {
      case 'services': setStep('landing'); break;
      case 'operator': setStep('services'); break;
      case 'datetime': setStep(skipOperator ? 'services' : 'operator'); break;
      case 'client': setStep('datetime'); break;
      case 'confirm': setStep('client'); break;
      default: break;
    }
  }, [step, skipOperator]);

  const embedMinHeight = isEmbed ? undefined : '100vh';

  // ── Loading ──
  if (loading) {
    return (
      <div ref={contentRef} style={{ minHeight: embedMinHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isEmbed ? 'transparent' : '#fff', fontFamily: BODY_FONT, padding: isEmbed ? 40 : 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 16, border: '3px solid #E5E5EA', borderTopColor: '#0F6CBD', animation: 'kiosk-spin 600ms linear infinite' }} />
        <style>{`@keyframes kiosk-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  // ── Error ──
  if (error || !config) {
    return (
      <div ref={contentRef} style={{ minHeight: embedMinHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isEmbed ? 'transparent' : '#fff', fontFamily: BODY_FONT }}>
        <div style={{ textAlign: 'center', padding: 40, maxWidth: 400, margin: '0 24px' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="#E5E5EA" style={{ marginBottom: 16 }}>
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', marginBottom: 8 }}>Booking not available</div>
          <div style={{ fontSize: 15, color: '#636366', lineHeight: 1.5 }}>{error ?? 'This booking page doesn\'t exist.'}</div>
        </div>
      </div>
    );
  }

  // ── Landing page ──
  if (step === 'landing') {
    return (
      <LandingPage
        config={config} services={services} operators={operators} hours={hours}
        headingFont={headingFont} onBookNow={enterBookingFlow}
        onSelectService={handleSelectServiceFromLanding}
        onSelectOperator={handleSelectOperatorFromLanding}
      />
    );
  }

  // ── Success ──
  if (step === 'success' && selectedService && selectedTime) {
    return (
      <div ref={contentRef} style={{ minHeight: embedMinHeight, background: isEmbed ? 'transparent' : '#fff', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, maxWidth: 480, width: '100%', margin: '0 auto' }}>
          <KioskSuccess
            service={selectedService} operator={selectedOperator}
            date={selectedDate} time={selectedTime}
            autoResetSeconds={config.kioskAutoResetSeconds}
            accentColor={config.accentColor} onReset={resetAll}
          />
        </div>
        {isEmbed ? <EmbedFooter industry={config?.industry} /> : <KioskFooter industry={config?.industry} />}
      </div>
    );
  }

  // ── Booking flow steps ──
  return (
    <div ref={contentRef} style={{ minHeight: embedMinHeight, background: isEmbed ? 'transparent' : '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      <BookingHeader config={config} step={stepIndex} totalSteps={totalSteps} headingFont={headingFont} onBack={handleBack} />
      <div style={{ flex: 1, maxWidth: 480, width: '100%', margin: '0 auto', overflowY: 'auto' }}>
        {step === 'services' && (
          <KioskServicePicker services={services} accentColor={config.accentColor} showPrices={config.showPrices} headingFont={headingFont} isNewClient={isNewClient} onSelect={handleSelectService} />
        )}
        {step === 'operator' && (
          <KioskOperatorPicker operators={operators} selectionMode={config.operatorSelectionMode === 'required' ? 'required' : 'optional'} accentColor={config.accentColor} headingFont={headingFont} onSelect={handleSelectOperator} />
        )}
        {step === 'datetime' && (
          <KioskDateTimePicker selectedDate={selectedDate} onDateChange={setSelectedDate} slots={slots} closed={closed} loading={slotsLoading} accentColor={config.accentColor} headingFont={headingFont} onSelectTime={handleSelectTime} />
        )}
        {step === 'client' && (
          <KioskClientForm name={clientName} phone={clientPhone} notes={clientNotes} onNameChange={setClientName} onPhoneChange={setClientPhone} onNotesChange={setClientNotes} accentColor={config.accentColor} headingFont={headingFont} onContinue={async () => {
            setStep('confirm');
            if (config && clientPhone.trim()) {
              const nc = await checkIsNewClient(config.businessId, clientPhone.trim());
              setIsNewClient(nc);
            }
          }} />
        )}
        {step === 'confirm' && selectedService && selectedTime && (
          <KioskConfirmation service={selectedService} operator={selectedOperator} date={selectedDate} time={selectedTime} clientName={clientName} clientPhone={clientPhone} accentColor={config.accentColor} headingFont={headingFont} cancellationPolicy={config.cancellationPolicy} submitting={submitting} error={submitError} dynamicPrice={dynamicPrice} onConfirm={handleConfirm} onBack={handleBack} onRetryTime={() => { setSubmitError(null); setStep('datetime'); }} />
        )}
      </div>
      {isEmbed ? <EmbedFooter industry={config?.industry} /> : <KioskFooter industry={config?.industry} />}
    </div>
  );
}

// ── Embed-only footer (minimal Powered by CrewBit) ──
function EmbedFooter({ industry: _industry }: { industry?: string | null }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 24px', fontSize: 11, color: '#ADB5BD', fontFamily: BODY_FONT }}>
      <a href="https://crewbit.ca" target="_blank" rel="noopener noreferrer" style={{
        color: '#ADB5BD', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="#ADB5BD"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
        Powered by CrewBit
      </a>
    </div>
  );
}
